import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList} from '@/hooks/common/use-paged-list'
import {track} from '@/lib/telemetry'
import {uploadToStorage} from '@/hooks/sgsst/evidencias/use-evidence'
import type {components} from '@/lib/api-schema'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'

export type Entrevista = components['schemas']['EntrevistaRead']
export type RegistrationLink = components['schemas']['RegistrationLinkRead']
export type Hallazgo = components['schemas']['HallazgoRead']
export type EntrevistaControl = components['schemas']['ControlRead']
export type HallazgoFoto = components['schemas']['HallazgoFotoRead']
export type EntrevistaVersion = components['schemas']['EntrevistaVersionSummary']
export type ReSolicitarTodasResult = components['schemas']['ReSolicitarTodasRead']

// El primer GET arranca el cuestionario (GTC 45, determinista en el backend); los siguientes
// traen la transcripción completa.
export function useMyEntrevista(businessId: string | undefined) {
  return useQuery({
    queryKey: ['entrevista', businessId, 'me'],
    queryFn: async () => (await api.get<Entrevista>(`/businesses/${businessId}/mi/entrevista`)).data,
    enabled: !!businessId,
  })
}

// Solo se acepta `opcion`, una de las ofrecidas por la pregunta actual: nada de texto libre.
export function usePostEntrevistaTurno(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {opcion: string}) =>
      (await api.post<Entrevista>(`/businesses/${businessId}/mi/entrevista/turnos`, payload)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(['entrevista', businessId, 'me'], data)
      queryClient.invalidateQueries({queryKey: ['mi-onboarding', businessId]})
      if (data.completed_at) track('entrevista_completed', {business_id: businessId})
    },
  })
}

// El único endpoint con texto libre: solo vale en la pregunta abierta del cierre.
export function usePostEntrevistaTextoLibre(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {texto: string}) =>
      (await api.post<Entrevista>(`/businesses/${businessId}/mi/entrevista/turnos/texto-libre`, payload)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(['entrevista', businessId, 'me'], data)
      queryClient.invalidateQueries({queryKey: ['mi-onboarding', businessId]})
      if (data.completed_at) track('entrevista_completed', {business_id: businessId})
    },
  })
}

// La respuesta es la entrevista rebobinada y se siembra tal cual. 409 si ya se completó o en
// la primera pregunta.
export function useDeshacerEntrevistaTurno(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      (await api.post<Entrevista>(`/businesses/${businessId}/mi/entrevista/turnos/deshacer`, {})).data,
    onSuccess: (data) => {
      queryClient.setQueryData(['entrevista', businessId, 'me'], data)
      queryClient.invalidateQueries({queryKey: ['mi-onboarding', businessId]})
      track('entrevista_turno_deshecho', {business_id: businessId})
    },
  })
}

async function mintHallazgoFotoUploadTarget(businessId: string, hallazgoId: string, file: File) {
  return (
    await api.post<components['schemas']['FotoUploadTargetRead']>(
      `/businesses/${businessId}/mi/entrevista/hallazgos/${hallazgoId}/foto-uploads`,
      {filename: file.name, content_type: file.type, size_bytes: file.size}
    )
  ).data
}

export function useUploadHallazgoFoto(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      hallazgoId,
      file,
      onProgress,
    }: {
      hallazgoId: string
      file: File
      onProgress: (pct: number) => void
    }) => {
      const target = await mintHallazgoFotoUploadTarget(businessId, hallazgoId, file)
      await uploadToStorage(target, file, onProgress)
      return (
        await api.post<HallazgoFoto[]>(`/businesses/${businessId}/mi/entrevista/hallazgos/${hallazgoId}/fotos`, {
          object_key: target.object_key,
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size,
        })
      ).data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['entrevista', businessId, 'me']})
      // La tarjeta de evidencia pendiente del inicio lee los mismos hallazgos: se limpia en ambas.
      queryClient.invalidateQueries({queryKey: ['member', businessId, 'evidencia-pendiente']})
    },
  })
}

async function mintControlFotoUploadTarget(businessId: string, controlId: string, file: File) {
  return (
    await api.post<components['schemas']['ControlFotoUploadTargetRead']>(
      `/businesses/${businessId}/mi/controles/${controlId}/foto-uploads`,
      {filename: file.name, content_type: file.type, size_bytes: file.size}
    )
  ).data
}

export function useUploadMyControlFoto(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      controlId,
      file,
      onProgress,
    }: {
      controlId: string
      file: File
      onProgress: (pct: number) => void
    }) => {
      const target = await mintControlFotoUploadTarget(businessId, controlId, file)
      await uploadToStorage(target, file, onProgress)
      return (
        await api.post<components['schemas']['ControlFotoRead'][]>(
          `/businesses/${businessId}/mi/controles/${controlId}/fotos`,
          {
            object_key: target.object_key,
            filename: file.name,
            content_type: file.type,
            size_bytes: file.size,
          }
        )
      ).data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['member', businessId, 'evidencia-pendiente']})
    },
  })
}

// Las fotos de toda la versión en una petición, por hallazgo_id (PYTHON-1W).
export function useEntrevistaFotos(
  businessId: string | undefined,
  employeeId: string | undefined,
  entrevistaId: string | undefined
) {
  return useQuery({
    queryKey: ['entrevista', businessId, employeeId, entrevistaId, 'fotos'],
    queryFn: async () =>
      (
        await api.get<Record<string, HallazgoFoto[]>>(
          `/businesses/${businessId}/employees/${employeeId}/entrevista/${entrevistaId}/fotos`
        )
      ).data,
    enabled: !!businessId && !!employeeId && !!entrevistaId,
  })
}

// La respuesta es la lista de fotos del hallazgo y se funde en la caché de `useEntrevistaFotos`.
export function useHallazgoFotoAnalysisAction(businessId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      employeeId,
      hallazgoId,
      fotoId,
      action,
    }: {
      employeeId: string
      entrevistaId: string
      hallazgoId: string
      fotoId: string
      action: 'dismiss' | 'reanalyze'
    }) =>
      (
        await api.post<HallazgoFoto[]>(
          `/businesses/${businessId}/employees/${employeeId}/entrevista/hallazgos/${hallazgoId}/fotos/${fotoId}/analysis/${action}`
        )
      ).data,
    onSuccess: (fotos, {employeeId, entrevistaId, hallazgoId}) => {
      queryClient.setQueryData<Record<string, HallazgoFoto[]>>(
        ['entrevista', businessId, employeeId, entrevistaId, 'fotos'],
        (previo) => ({...previo, [hallazgoId]: fotos})
      )
    },
  })
}

export function useEmployeeEntrevista(businessId: string | undefined, employeeId: string | undefined) {
  return useQuery({
    queryKey: ['entrevista', businessId, employeeId],
    queryFn: async () =>
      (await api.get<Entrevista>(`/businesses/${businessId}/employees/${employeeId}/entrevista`)).data,
    enabled: !!businessId && !!employeeId,
  })
}

// `retry: false`: el 409 de perfil incompleto lo arregla el dueño, no un reintento.
export function useRegistrationLink(businessId: string | undefined) {
  return useQuery({
    queryKey: ['registration-link', businessId],
    queryFn: async () => (await api.get<RegistrationLink>(`/businesses/${businessId}/registro-link`)).data,
    enabled: !!businessId,
    retry: false,
  })
}

export function useRegenerateRegistrationLink(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      (await api.post<RegistrationLink>(`/businesses/${businessId}/registro-link/regenerate`)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(['registration-link', businessId], data)
      track('registration_link_regenerated', {business_id: businessId})
    },
  })
}

// Aprobar reconcilia la matriz en el servidor y puede crear área o cargo: por eso invalida
// `ipevr` y `tags`. Sin cuerpo es «Aprobar sin ajustar».
export type AprobacionEstructuracion = components['schemas']['AprobacionEstructuracion']

export function useApproveEmployee(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({employeeId, estructuracion}: {employeeId: string; estructuracion?: AprobacionEstructuracion}) =>
      (
        await api.post<Employee>(
          `/businesses/${businessId}/employees/${employeeId}/aprobar`,
          estructuracion ?? undefined
        )
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['employees', businessId]})
      queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
      queryClient.invalidateQueries({queryKey: ['ipevr', businessId]})
      queryClient.invalidateQueries({queryKey: ['tags', businessId]})
      track('employee_approved', {business_id: businessId})
    },
  })
}

export function useRejectEmployee(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (employeeId: string) => api.post(`/businesses/${businessId}/employees/${employeeId}/rechazar`),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['employees', businessId]})
      queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
      track('employee_rejected', {business_id: businessId})
    },
  })
}

const HISTORIAL_PAGE_SIZE = 10

export function useEmployeeEntrevistaHistorial(
  businessId: string | undefined,
  employeeId: string | undefined,
  page = 1
) {
  return usePagedList<EntrevistaVersion>(
    ['entrevista', businessId, employeeId, 'historial'],
    businessId && employeeId ? `/businesses/${businessId}/employees/${employeeId}/entrevista/historial` : undefined,
    {page, search: ''},
    HISTORIAL_PAGE_SIZE
  )
}

export function useEmployeeEntrevistaVersion(
  businessId: string | undefined,
  employeeId: string | undefined,
  entrevistaId: string | undefined
) {
  return useQuery({
    queryKey: ['entrevista', businessId, employeeId, 'historial', entrevistaId],
    queryFn: async () =>
      (
        await api.get<Entrevista>(
          `/businesses/${businessId}/employees/${employeeId}/entrevista/historial/${entrevistaId}`
        )
      ).data,
    enabled: !!businessId && !!employeeId && !!entrevistaId,
  })
}

export function useMyEntrevistaHistorial(businessId: string | undefined, page = 1) {
  return usePagedList<EntrevistaVersion>(
    ['entrevista', businessId, 'me', 'historial'],
    businessId ? `/businesses/${businessId}/mi/entrevista/historial` : undefined,
    {page, search: ''},
    HISTORIAL_PAGE_SIZE
  )
}

export function useMyEntrevistaVersion(businessId: string | undefined, entrevistaId: string | undefined) {
  return useQuery({
    queryKey: ['entrevista', businessId, 'me', 'historial', entrevistaId],
    queryFn: async () =>
      (await api.get<Entrevista>(`/businesses/${businessId}/mi/entrevista/historial/${entrevistaId}`)).data,
    enabled: !!businessId && !!entrevistaId,
  })
}

export function useReSolicitarEvaluacion(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (employeeId: string) =>
      (await api.post<Employee>(`/businesses/${businessId}/employees/${employeeId}/re-solicitar-evaluacion`)).data,
    onSuccess: (_data, employeeId) => {
      queryClient.invalidateQueries({queryKey: ['employees', businessId]})
      queryClient.invalidateQueries({queryKey: ['entrevista', businessId, employeeId]})
      track('re_solicitar_evaluacion', {business_id: businessId})
    },
  })
}

export function useReSolicitarTodas(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      (await api.post<ReSolicitarTodasResult>(`/businesses/${businessId}/entrevistas/re-solicitar-todas`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['employees', businessId]})
      queryClient.invalidateQueries({queryKey: ['entrevista', businessId]})
      track('re_solicitar_todas', {business_id: businessId})
    },
  })
}
